import React, { useState, useMemo, useCallback } from 'react';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { useCrewChanges } from '../../hooks/useCrewChanges';
import { useEmployees } from '../../hooks/useEmployees';
import { useRanks } from '../../hooks/useRanks';
import { CrewChange } from '../../types';
import { PageHeader, ErrorState } from '../../components/Common';
import { HistoryEvent, EmployeeSummary, statusToEventType } from './types';
import SummaryFilters from './SummaryFilters';
import HistoryFilters from './HistoryFilters';
import SummaryTable from './SummaryTable';
import HistoryTable from './HistoryTable';
import DetailsModal from './DetailsModal';
import StatsBar from './StatsBar';

const CrewEmployeeHistory: React.FC = () => {
  const crewChangesOptions = useMemo(() => ({ populate: '*' }), []);
  const employeesOptions = useMemo(() => ({ populate: '*' }), []);
  const ranksOptions = useMemo(() => ({ populate: '*' }), []);

  const { data: crewChangesData = [], isLoading: loadingCrewChanges, error: crewChangesError } = useCrewChanges(crewChangesOptions);
  const { data: employees = [], isLoading: loadingEmployees, error: employeesError } = useEmployees(employeesOptions);
  const { data: ranks = [], isLoading: loadingRanks, error: ranksError } = useRanks(ranksOptions);

  const isLoading = loadingCrewChanges || loadingEmployees || loadingRanks;
  const error = crewChangesError || employeesError || ranksError;

  const getEmployeeName = useCallback((employeeId: string) => {
    const employee = employees.find((e: any) => e.id?.toString() === employeeId || e.documentId === employeeId);
    return employee ? employee.name : 'Unknown';
  }, [employees]);

  const getEmployeeRank = useCallback((employeeId: string) => {
    const employee = employees.find((e: any) => e.id?.toString() === employeeId || e.documentId === employeeId);
    if (!employee) return 'Unknown';
    const rankEntry = ranks.find((r: any) => r.id?.toString() === employee.rank || r.documentId === employee.rank || r.name === employee.rank);
    return rankEntry ? rankEntry.name : (employee.rank || employee.position || 'Unknown');
  }, [employees, ranks]);

  const getBranchName = useCallback((cc: CrewChange) => {
    if (cc.branchData) return cc.branchData.name;
    return undefined;
  }, []);

  const getBranchLocation = useCallback((cc: CrewChange) => {
    if (cc.branchData) return cc.branchData.location;
    return undefined;
  }, []);

  const historyEvents: HistoryEvent[] = useMemo(() => {
    return (crewChangesData as CrewChange[]).map(cc => {
      const empId = cc.employeeData?.id?.toString() || cc.employee?.toString() || cc.employeeId || '';
      return {
        id: cc.id?.toString(),
        employeeId: empId,
        employeeName: cc.employeeData?.name || getEmployeeName(empId),
        eventType: statusToEventType(cc.status),
        vesselId: cc.branchData?.id?.toString() || cc.branch?.toString() || cc.branchId,
        vesselName: getBranchName(cc),
        eventDate: cc.changeDate,
        location: getBranchLocation(cc),
        notes: cc.notes,
        createdAt: cc.createdAt || cc.changeDate
      };
    });
  }, [crewChangesData, getEmployeeName, getBranchName, getBranchLocation]);

  const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'summary' | 'history'>('summary');
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('all');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');
  const [showDetailsPopup, setShowDetailsPopup] = useState(false);
  const [detailsType, setDetailsType] = useState<'sign-ons' | 'sign-offs' | null>(null);
  const [selectedEmployeeForDetails, setSelectedEmployeeForDetails] = useState<EmployeeSummary | null>(null);

  const [summarySearchTerm, setSummarySearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [rankFilter, setRankFilter] = useState('');
  const [activityFilter, setActivityFilter] = useState('');

  const employeeSummaries = useMemo((): EmployeeSummary[] => {
    const summaries: { [key: string]: EmployeeSummary } = {};
    historyEvents.forEach(event => {
      if (!summaries[event.employeeId]) {
        summaries[event.employeeId] = {
          employeeId: event.employeeId,
          employeeName: event.employeeName,
          rank: getEmployeeRank(event.employeeId),
          totalSignOns: 0, totalSignOffs: 0, totalVacations: 0,
          currentStatus: 'off-duty',
          lastActivity: event.eventDate,
        };
      }
      const summary = summaries[event.employeeId];
      if (event.eventType === 'sign-on') { summary.totalSignOns++; summary.currentStatus = 'on-vessel'; summary.currentVessel = event.vesselName; }
      else if (event.eventType === 'sign-off') { summary.totalSignOffs++; summary.currentStatus = 'off-duty'; summary.currentVessel = undefined; }
      else if (event.eventType === 'vacation-start') { summary.totalVacations++; summary.currentStatus = 'on-vacation'; }
      else if (event.eventType === 'vacation-end') { summary.currentStatus = 'off-duty'; }
      if (new Date(event.eventDate) > new Date(summary.lastActivity)) { summary.lastActivity = event.eventDate; }
    });
    return Object.values(summaries);
  }, [historyEvents, getEmployeeRank]);

  const filteredEmployeeSummaries = useMemo(() => {
    return employeeSummaries.filter(summary => {
      const matchesSearch = summary.employeeName.toLowerCase().includes(summarySearchTerm.toLowerCase()) ||
                           summary.rank.toLowerCase().includes(summarySearchTerm.toLowerCase());
      const matchesStatus = statusFilter === '' || summary.currentStatus === statusFilter;
      const matchesRank = rankFilter === '' || summary.rank === rankFilter;
      const matchesActivity = activityFilter === '' || (() => {
        const daysSinceActivity = Math.floor((new Date().getTime() - new Date(summary.lastActivity).getTime()) / (1000 * 60 * 60 * 24));
        switch (activityFilter) {
          case 'recent': return daysSinceActivity <= 7;
          case 'moderate': return daysSinceActivity > 7 && daysSinceActivity <= 30;
          case 'old': return daysSinceActivity > 30;
          default: return true;
        }
      })();
      return matchesSearch && matchesStatus && matchesRank && matchesActivity;
    });
  }, [employeeSummaries, summarySearchTerm, statusFilter, rankFilter, activityFilter]);

  const uniqueRanks = useMemo(() => {
    const ranksArr = employeeSummaries.map(s => s.rank);
    const unique: string[] = [];
    ranksArr.forEach(r => { if (!unique.includes(r)) unique.push(r); });
    return unique.sort();
  }, [employeeSummaries]);

  const filteredEvents = useMemo(() => {
    let filtered = selectedEmployee
      ? historyEvents.filter(event => event.employeeId === selectedEmployee)
      : historyEvents;
    if (eventTypeFilter !== 'all') filtered = filtered.filter(event => event.eventType === eventTypeFilter);
    if (dateFromFilter) filtered = filtered.filter(event => event.eventDate >= dateFromFilter);
    if (dateToFilter) filtered = filtered.filter(event => event.eventDate <= dateToFilter);
    return filtered.sort((a, b) => new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime());
  }, [historyEvents, selectedEmployee, eventTypeFilter, dateFromFilter, dateToFilter]);

  const totalStats = useMemo(() => {
    const dataToUse = viewMode === 'summary' ? filteredEmployeeSummaries : employeeSummaries;
    return {
      totalEmployees: dataToUse.length,
      totalSignOns: dataToUse.reduce((sum, emp) => sum + emp.totalSignOns, 0),
      totalSignOffs: dataToUse.reduce((sum, emp) => sum + emp.totalSignOffs, 0),
      totalVacations: dataToUse.reduce((sum, emp) => sum + emp.totalVacations, 0),
      activeCrewMembers: dataToUse.filter(emp => emp.currentStatus === 'on-vessel').length,
      onVacation: dataToUse.filter(emp => emp.currentStatus === 'on-vacation').length,
    };
  }, [employeeSummaries, filteredEmployeeSummaries, viewMode]);

  const handleClearSummaryFilters = () => { setSummarySearchTerm(''); setStatusFilter(''); setRankFilter(''); setActivityFilter(''); };
  const handleFilterToday = () => { const today = new Date().toISOString().split('T')[0]; setDateFromFilter(today); setDateToFilter(today); };
  const handleClearHistoryFilters = () => { setSelectedEmployee(null); setEventTypeFilter('all'); setDateFromFilter(''); setDateToFilter(''); };

  const handleShowDetails = (employee: EmployeeSummary, type: 'sign-ons' | 'sign-offs') => {
    setSelectedEmployeeForDetails(employee);
    setDetailsType(type);
    setShowDetailsPopup(true);
  };

  const handleCloseDetails = () => { setShowDetailsPopup(false); setSelectedEmployeeForDetails(null); setDetailsType(null); };

  const getEmployeeEvents = (employeeId: string, eventType: 'sign-on' | 'sign-off') =>
    historyEvents.filter(event => event.employeeId === employeeId && event.eventType === eventType);

  const fmtLabel = (s: string) => s.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());
  const fmtSummary = (s: EmployeeSummary) => ({ 'Employee Name': s.employeeName, 'Rank': s.rank, 'Total Sign Ons': s.totalSignOns, 'Total Sign Offs': s.totalSignOffs, 'Total Vacations': s.totalVacations, 'Current Status': fmtLabel(s.currentStatus), 'Current Vessel': s.currentVessel || 'N/A', 'Last Activity': new Date(s.lastActivity).toLocaleDateString() });
  const fmtHistory = (e: HistoryEvent) => ({ 'Employee Name': e.employeeName, 'Event Type': fmtLabel(e.eventType), 'Vessel Name': e.vesselName || 'N/A', 'Event Date': new Date(e.eventDate).toLocaleDateString(), 'Location': e.location, 'Notes': e.notes || 'N/A', 'Created Date': new Date(e.createdAt).toLocaleDateString() });
  const dateStamp = () => new Date().toISOString().split('T')[0];

  const handleExport = () => {
    if (viewMode === 'summary') exportToExcel(filteredEmployeeSummaries.map(fmtSummary), `crew_employee_summary_${dateStamp()}`);
    else exportToExcel(filteredEvents.map(fmtHistory), `crew_employee_history_${dateStamp()}`);
  };
  const handlePrint = () => {
    if (viewMode === 'summary') printAsExcel(filteredEmployeeSummaries.map(fmtSummary), 'Crew Employee Summary');
    else printAsExcel(filteredEvents.map(fmtHistory), 'Crew Employee History');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading crew history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return <ErrorState message={error.message} entityName="crew history" />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Crew Employee History"
        description="Track crew sign-on/off events, vessel assignments, and vacation periods"
        actions={[
          { label: `Export ${viewMode === 'summary' ? 'Summary' : 'History'}`, onClick: handleExport, variant: 'success' },
          { label: `Print ${viewMode === 'summary' ? 'Summary' : 'History'}`, onClick: handlePrint, variant: 'info' },
        ]}
      >
        <button
          onClick={() => { setViewMode('summary'); setSelectedEmployee(null); }}
          className={`px-4 py-2 rounded-md font-medium ${viewMode === 'summary' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
        >
          Summary View
        </button>
        <button
          onClick={() => setViewMode('history')}
          className={`px-4 py-2 rounded-md font-medium ${viewMode === 'history' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
        >
          History View
        </button>
      </PageHeader>

      <StatsBar {...totalStats} />

      {viewMode === 'history' && (
        <HistoryFilters
          selectedEmployee={selectedEmployee}
          setSelectedEmployee={setSelectedEmployee}
          eventTypeFilter={eventTypeFilter}
          setEventTypeFilter={setEventTypeFilter}
          dateFromFilter={dateFromFilter}
          setDateFromFilter={setDateFromFilter}
          dateToFilter={dateToFilter}
          setDateToFilter={setDateToFilter}
          employeeSummaries={employeeSummaries}
          onFilterToday={handleFilterToday}
          onClearFilters={handleClearHistoryFilters}
        />
      )}

      {viewMode === 'summary' && (
        <SummaryFilters
          summarySearchTerm={summarySearchTerm}
          setSummarySearchTerm={setSummarySearchTerm}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          rankFilter={rankFilter}
          setRankFilter={setRankFilter}
          activityFilter={activityFilter}
          setActivityFilter={setActivityFilter}
          uniqueRanks={uniqueRanks}
          onClearFilters={handleClearSummaryFilters}
          filteredCount={filteredEmployeeSummaries.length}
          totalCount={employeeSummaries.length}
        />
      )}

      {viewMode === 'summary' ? (
        <SummaryTable
          data={filteredEmployeeSummaries}
          onViewHistory={(emp) => { setSelectedEmployee(emp.employeeId); setViewMode('history'); }}
          onShowDetails={handleShowDetails}
        />
      ) : (
        <HistoryTable
          data={filteredEvents}
          selectedEmployee={selectedEmployee}
          employeeSummaries={employeeSummaries}
          onBackToSummary={() => { setSelectedEmployee(null); setViewMode('summary'); }}
        />
      )}

      {showDetailsPopup && selectedEmployeeForDetails && detailsType && (
        <DetailsModal
          employee={selectedEmployeeForDetails}
          detailsType={detailsType}
          events={getEmployeeEvents(
            selectedEmployeeForDetails.employeeId,
            detailsType === 'sign-ons' ? 'sign-on' : 'sign-off'
          )}
          onClose={handleCloseDetails}
        />
      )}
    </div>
  );
};

export default CrewEmployeeHistory;
